// Kanban board: one column per status, native HTML5 drag-and-drop to change a
// ticket's status, filters and open/closed counters.
import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  listTickets, getSettings, updateTicket, getAssignableUsers,
  type Ticket, type ServiceDeskSettings, type SlaState,
} from "../services/serviceDeskService";
import { SlaBadge, PriorityBadge, AiAgentBadge, ReworkChip, AgentAvatar, SampleChip } from "../components/Badges";
import { DeskLoadErrorPanel, useDeskLoadError } from "../components/DeskLoadError";
import { SERVICE_DESK_BASE } from "../config";
import { useUserStore } from "../../../store/userStore";
import { useAlert } from "../../../context/AlertContext";
import {
  apiErrorMessage, canCloseTicket, formatClosureDate, CLOSED_STATUS, PENDING_CLOSURE_STATUS,
} from "../services/ticketClosure";
import {
  trackTicketStatusChanged,
  trackTicketStatusChangeFailed,
} from "../../../analytics/serviceDeskAnalytics";

// "Rehacer" (CC-CONSOLE #1337) is pending work: the requester said it was not
// solved. Missing here it would count as closed, since `closed` is the remainder.
const OPEN = ["open", "in_progress", "waiting_customer", "rework"];

export default function KanbanSection() {
  const { t } = useTranslation("serviceDesk");
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);
  const { showAlert } = useAlert();
  const [settings, setSettings] = useState<ServiceDeskSettings | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState<string | null>(null);
  const [filters, setFilters] = useState({ brand: "", priority: "", label: "", sla: "" });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const topScrollRef = useRef<HTMLDivElement>(null);
  // Igual que la lista: sin captura, el 403 de agente dejaba un tablero vacío
  // sin explicación (y una promesa rechazada sin manejar).
  const { error: loadError, capture, reset } = useDeskLoadError();

  const load = () => {
    reset();
    return listTickets({ scope: "all", limit: 500, ...cleanFilters(filters) })
      .then((r) => setTickets(r.data))
      .catch(capture)
      .finally(() => setLoading(false));
  };

  useEffect(() => { getSettings().then(setSettings).catch(() => {}); }, []);
  // Nombre ACTUAL de cada agente asignado (gana al guardado en el ticket).
  const [agentNames, setAgentNames] = useState<Record<string, string>>({});
  useEffect(() => {
    getAssignableUsers()
      .then((users) => setAgentNames(Object.fromEntries(users.map((u) => [u.id, u.name]))))
      .catch(() => {});
  }, []);
  useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [filters]);

  const columns = settings?.statuses || [];
  const counts = useMemo(() => {
    const open = tickets.filter((t) => OPEN.includes(t.status)).length;
    return { open, closed: tickets.length - open, total: tickets.length };
  }, [tickets]);

  const handleTopScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const handleContainerScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (topScrollRef.current) {
      topScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const onDrop = async (statusId: string) => {
    if (!dragId) return;
    const ticket = tickets.find((t) => t.id === dragId);
    setDragId(null);
    if (!ticket || ticket.status === statusId) return;
    // Closing belongs to whoever opened the ticket (CC-CONSOLE #1337). Decided
    // here from the card's data so the reason shows on drop; otherwise the card
    // jumped to "Cerrado", the 403 arrived and it went back without a word.
    if (statusId === CLOSED_STATUS) {
      const permission = canCloseTicket(ticket, { id: user?.id, email: user?.email }, Date.now());
      if (!permission.allowed) {
        const text =
          permission.reason === "locked" && permission.unlockAt == null
            ? t("closure.denied.lockedNoDate", { days: ticket.closure?.lockDays ?? 7 })
            : t(`closure.denied.${permission.reason}`, {
                date: formatClosureDate(permission.unlockAt),
                days: ticket.closure?.lockDays ?? 7,
              });
        showAlert({ icon: "info", title: t("closure.denied.title"), text });
        return;
      }
    }
    // optimistic
    setTickets((prev) => prev.map((t) => (t.id === dragId ? { ...t, status: statusId } : t)));
    try {
      await updateTicket(ticket.id, { status: statusId });
      // `method` separa el arrastre del kanban del selector del detalle: son
      // dos flujos de UI distintos y hoy no se sabe cual usa la gente.
      trackTicketStatusChanged({
        fromStatus: String(ticket.status ?? "unknown"),
        toStatus: statusId,
        priority: String(ticket.priority ?? "unknown"),
        escalated: Boolean(ticket.escalated),
        slaState: String(ticket.slaStatus?.overall ?? "unknown"),
        method: "kanban_drag",
      });
      load();
    } catch (err: unknown) {
      trackTicketStatusChangeFailed({
        toStatus: statusId,
        method: "kanban_drag",
      });
      load(); // revert by reloading truth
      // The backend sends the reason ready to show (transition not allowed,
      // a close that is not theirs to make).
      showAlert({ icon: "error", title: t("closure.moveError"), text: apiErrorMessage(err) });
    }
  };

  const card = "bg-surface border-line";
  const text = "text-content";

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className={`text-xl font-bold ${text}`}>{t("kanban.title")}</h1>
        <div className="flex items-center gap-3 text-sm">
          <span className={`rounded-full px-3 py-1 font-semibold bg-info/15 text-info`}>{t("kanban.open")}: {counts.open}</span>
          <span className={`rounded-full px-3 py-1 font-semibold bg-subtle text-content-secondary`}>{t("kanban.closed")}: {counts.closed}</span>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <Select value={filters.brand} onChange={(v) => setFilters((f) => ({ ...f, brand: v }))} placeholder={t("kanban.filters.allBrands")} options={(settings?.brands || []).map((b) => ({ value: b.id, label: b.label }))} />
        <Select value={filters.priority} onChange={(v) => setFilters((f) => ({ ...f, priority: v }))} placeholder={t("kanban.filters.allPriorities")} options={[["low", "Baja"], ["medium", "Media"], ["high", "Alta"], ["urgent", "Urgente"]].map(([value, label]) => ({ value, label }))} />
        <Select value={filters.label} onChange={(v) => setFilters((f) => ({ ...f, label: v }))} placeholder={t("kanban.filters.allLabels")} options={(settings?.labels || []).map((l) => ({ value: l.id, label: l.label }))} />
        <Select value={filters.sla} onChange={(v) => setFilters((f) => ({ ...f, sla: v }))} placeholder={t("kanban.filters.allSla")} options={[["on_time", t("kanban.filters.sla.onTime")], ["at_risk", t("kanban.filters.sla.atRisk")], ["breached", t("kanban.filters.sla.breached")]].map(([value, label]) => ({ value, label }))} />
      </div>

      {loadError ? (
        <DeskLoadErrorPanel error={loadError} />
      ) : loading ? (
        <p className="text-gray-400">{t("kanban.loading")}</p>
      ) : (
        <>
          {/* Scrollbar superior duplicado */}
          <div
            ref={topScrollRef}
            className="overflow-x-auto mb-2 border-b border-line"
            onScroll={handleTopScroll}
            style={{
              height: "20px",
              overflowX: "auto",
              overflowY: "hidden",
            }}
          >
            <div className="flex gap-4" style={{ width: "max-content" }}>
              {columns.map((col) => (
                <div key={`top-${col.id}`} className="w-72 flex-shrink-0"></div>
              ))}
            </div>
          </div>

          {/* Contenedor principal con scrollbar inferior */}
          <div ref={scrollContainerRef} className="flex gap-4 overflow-x-auto pb-4" onScroll={handleContainerScroll}>
            {columns.map((col) => {
              const colTickets = tickets.filter((t) => t.status === col.id);
              return (
                <div
                  key={col.id}
                  className="w-72 flex-shrink-0"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(col.id)}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm font-semibold" style={{ color: col.color }}>
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: col.color }} />
                      {col.label}
                    </span>
                    <span className="text-xs text-gray-400">{colTickets.length}</span>
                  </div>
                  <div className={`min-h-[120px] space-y-2 rounded-xl border border-dashed p-2 border-line`}>
                    {colTickets.map((ticket) => (
                      <div
                        key={ticket.id}
                        draggable
                        onDragStart={() => setDragId(ticket.id)}
                        onClick={() => navigate(`${SERVICE_DESK_BASE}/tickets/${ticket.id}`)}
                        className={`cursor-pointer rounded-lg border p-3 shadow-sm transition hover:shadow ${card}`}
                      >
                        <div className="mb-1 flex items-center justify-between">
                          <span className="text-[11px] font-bold text-gray-400">{ticket.reference}</span>
                          <PriorityBadge priority={ticket.priority} />
                        </div>
                        <p className={`text-sm font-medium ${text}`}>{ticket.title}</p>
                        <p className="mt-0.5 text-[11px] text-gray-400">
                          {ticket.brandLabel}
                          {ticket.companyName ? ` · ${ticket.companyName}` : ""}
                        </p>
                        {(() => {
                          // El nombre actual del usuario asignado gana al guardado
                          // en el ticket (que sólo traen los de ejemplo).
                          const name = (ticket.assignedAgentId && agentNames[ticket.assignedAgentId]) || ticket.assignedAgentName;
                          return name ? (
                            <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-content-muted">
                              <AgentAvatar name={name} size={18} />
                              {name}
                            </p>
                          ) : null;
                        })()}
                        {ticket.sandbox && <div className="mt-1"><SampleChip ticket={ticket} /></div>}
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-1">
                          <SlaBadge state={(ticket.slaStatus?.overall || ticket.sla?.overall) as SlaState} />
                          {ticket.escalated && <span className="text-[11px] font-semibold text-red-500">{t("kanban.escalated")}</span>}
                        </div>
                        {ticket.aiAgent?.state && ticket.aiAgent.state !== "idle" && (
                          <div className="mt-1"><AiAgentBadge state={ticket.aiAgent.state} /></div>
                        )}
                        {Boolean(ticket.rework?.count) && (
                          <div className="mt-1"><ReworkChip count={ticket.rework?.count} /></div>
                        )}
                        {ticket.status === PENDING_CLOSURE_STATUS && (
                          <p className="mt-1 text-[11px] leading-snug text-amber-600">
                            {t("closure.pendingRequester")}
                            {ticket.closure?.unlocked
                              ? ` · ${t("closure.unlocked")}`
                              : ticket.closure?.unlockAt != null
                                ? ` · ${t("closure.unlocksOn", { date: formatClosureDate(ticket.closure.unlockAt) })}`
                                : ""}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function cleanFilters(f: Record<string, string>) {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(f)) if (v) out[k] = v;
  return out;
}

function Select({ value, onChange, placeholder, options }: { value: string; onChange: (v: string) => void; placeholder: string; options: { value: string; label: string }[] }) {
  return (
    <select className={`rounded-lg border px-3 py-1.5 text-sm !bg-app !border-line !text-content`} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
